// Bottom-row "monitor" pane.
//
// Single-line ticker showing fake CI/CD health metrics that subtly animate to
// give the page a sense of liveness — the same vibe as a dashboard pinned at
// the bottom of a real tmux session.
//
// Behavior:
//   - Every 4–7 seconds, bump `tests_passed` by 1–3.
//   - Roughly 1 in 4 ticks, also bump `builds` by 1.
//   - `deploys/wk` and `uptime` are static — they're shape-level metadata
//      rather than per-tick counters.
//   - Honors `prefers-reduced-motion: reduce` by skipping the interval
//      entirely (values stay frozen at their initial state).

import React, { useEffect, useState } from 'react';

interface MonitorPaneProps {
  isActive: boolean;
}

interface Metrics {
  testsPassed: number;
  builds: number;
}

const INITIAL: Metrics = { testsPassed: 2400, builds: 142 };
const DEPLOYS_PER_WEEK = 8;
const UPTIME = '99.97';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const MonitorPane: React.FC<MonitorPaneProps> = ({ isActive }) => {
  const [metrics, setMetrics] = useState<Metrics>(INITIAL);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let cancelled = false;
    let timer: number | null = null;

    function schedule() {
      // Random gap in [4000, 7000) ms so neighboring keys don't visibly tick
      // in lockstep — feels more like real telemetry.
      const next = 4000 + Math.floor(Math.random() * 3000);
      timer = window.setTimeout(() => {
        if (cancelled) return;
        setMetrics(prev => ({
          testsPassed: prev.testsPassed + 1 + Math.floor(Math.random() * 3),
          // ~25% chance of a build increment per tick.
          builds: prev.builds + (Math.random() < 0.25 ? 1 : 0),
        }));
        schedule();
      }, next);
    }
    schedule();

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  // Colors per spec: keys=amber, values=green, separators=gray.
  const sep = <span className="text-gray-500"> &middot; </span>;
  const eq = <span className="text-gray-500">=</span>;

  return (
    <div
      className="h-full w-full px-4 flex items-center"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: '12px',
      }}
    >
      {/* Spinning MODEC logo — anchors the monitor row visually and reminds
          recruiters that "currently @ MODEC" is the live state. CSS 3D
          transform; honors prefers-reduced-motion via the media query
          embedded in the inline <style> below. */}
      <style>{`
        @keyframes monitor-coin-spin {
          0%   { transform: rotateY(0deg);   }
          100% { transform: rotateY(360deg); }
        }
        .monitor-modec-coin {
          width: 20px;
          height: 20px;
          display: inline-block;
          transform-style: preserve-3d;
          animation: monitor-coin-spin 5s linear infinite;
          flex-shrink: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .monitor-modec-coin { animation: none; }
        }
      `}</style>
      <img
        src="/modec-logo.svg"
        alt="MODEC"
        className="monitor-modec-coin"
        title="Currently @ MODEC"
      />
      <span className="text-gray-500 ml-3">[</span>
      <span className="text-secondary-300">monitor</span>
      <span className="text-gray-500">]</span>
      <span className="ml-2">
        <span className="text-secondary-300">tests_passed</span>
        {eq}
        <span className="text-primary-400">{metrics.testsPassed}</span>
        {sep}
        <span className="text-secondary-300">builds</span>
        {eq}
        <span className="text-primary-400">{metrics.builds}</span>
        {sep}
        <span className="text-secondary-300">deploys/wk</span>
        {eq}
        <span className="text-primary-400">{DEPLOYS_PER_WEEK}</span>
        {sep}
        <span className="text-secondary-300">uptime</span>
        {eq}
        <span className="text-primary-400">{UPTIME}%</span>
      </span>
      {isActive && (
        <span className="ml-3 text-primary-400" aria-hidden="true">
          &#9608;
        </span>
      )}
    </div>
  );
};

export default MonitorPane;
